import React, { useMemo } from 'react';
import { Trade } from '../types';
import useCollection from '../hooks/useCollection';
import { computeConditionStats, ConditionStats } from '../utils/statsEngine';

const WinRateBar: React.FC<{ winRate: number; total: number }> = ({ winRate, total }) => {
  if (total === 0) return <span className="text-gray-300 text-xs">데이터 없음</span>;
  const color =
    winRate >= 65 ? 'bg-green-500' :
    winRate >= 55 ? 'bg-lime-400' :
    winRate >= 50 ? 'bg-yellow-400' :
    winRate >= 40 ? 'bg-orange-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${winRate}%` }} />
      </div>
      <span className={`text-sm font-bold w-10 text-right ${winRate >= 55 ? 'text-green-600' : winRate >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>
        {winRate}%
      </span>
    </div>
  );
};

const StatRow: React.FC<{ stat: ConditionStats; isHighlight: boolean }> = ({ stat, isHighlight }) => (
  <tr className={`transition-colors ${isHighlight ? 'bg-green-50/60' : 'hover:bg-white/40'}`}>
    <td className="px-4 py-3 text-sm font-medium text-gray-700">
      {isHighlight && <span className="mr-1">⭐</span>}
      {stat.label}
    </td>
    <td className="px-4 py-3 text-center text-sm text-gray-600">{stat.total}</td>
    <td className="px-4 py-3 text-center text-sm text-green-600 font-bold">{stat.wins}</td>
    <td className="px-4 py-3 text-center text-sm text-red-500 font-bold">{stat.losses}</td>
    <td className="px-4 py-3 text-center text-sm text-gray-400">{stat.breakevens}</td>
    <td className="px-4 py-3 min-w-[160px]">
      <WinRateBar winRate={stat.winRate} total={stat.total} />
    </td>
  </tr>
);

const EdgeStatsPage: React.FC = () => {
  const trades = useCollection<Trade>('trades', 'createdAt', 'desc');
  const stats = useMemo(() => computeConditionStats(trades), [trades]);

  const topEdges = useMemo(
    () => stats.filter(s => s.total >= 3 && s.winRate >= 60),
    [stats]
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Edge Stats</h2>
        <p className="text-sm text-gray-500 mt-0.5">조건별 승률을 분석해 실제 엣지를 찾아보세요 (최소 3건 이상 필요)</p>
      </div>

      {trades.length === 0 ? (
        <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow p-12 text-center text-gray-400">
          <p className="text-4xl mb-3">📈</p>
          <p className="font-medium">아직 거래 기록이 없습니다</p>
          <p className="text-sm mt-1">Trade Journal에서 거래를 기록하면 여기서 통계를 볼 수 있습니다</p>
        </div>
      ) : (
        <>
          {/* 발견된 엣지 하이라이트 */}
          {topEdges.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-green-700 mb-3">⭐ 발견된 엣지 (승률 60% 이상, 3건+)</h3>
              <div className="flex flex-wrap gap-2">
                {topEdges.map(s => (
                  <span key={s.label} className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-bold">
                    {s.label} — {s.winRate}% ({s.total}건)
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 전체 통계 테이블 */}
          <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 text-xs text-gray-500 border-b border-gray-100">
                    <th className="text-left px-4 py-3 font-semibold">조건</th>
                    <th className="text-center px-4 py-3 font-semibold">총 거래</th>
                    <th className="text-center px-4 py-3 font-semibold text-green-600">승</th>
                    <th className="text-center px-4 py-3 font-semibold text-red-500">패</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-400">무</th>
                    <th className="text-left px-4 py-3 font-semibold min-w-[180px]">승률</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {stats.map(stat => (
                    <StatRow
                      key={stat.label}
                      stat={stat}
                      isHighlight={stat.total >= 3 && stat.winRate >= 60}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-gray-400 text-center">
            총 {trades.length}건의 거래 기록 기준 · 최소 3건 이상인 조건만 엣지로 인정
          </p>
        </>
      )}
    </div>
  );
};

export default EdgeStatsPage;
